import {
  cloneMatch,
  createMatch,
  hashMatch,
  step,
  type InputBits,
  type MatchRules,
  type MatchState,
  type SimEvent,
} from "../../sim";
import { decode, encode, MAX_INPUTS_PER_PACKET } from "../protocol";
import type { Transport } from "../transport/transport";

export interface RollbackOptions {
  /** Своя задержка ввода, кадры (1–4 по пингу). */
  readonly inputDelay: number;
  /** Окно отката: дальше игра ждёт соперника. */
  readonly maxRollback: number;
  /** Раз в сколько кадров сверять суммы. */
  readonly checksumEvery: number;
}

export const DEFAULT_ROLLBACK: RollbackOptions = { inputDelay: 2, maxRollback: 8, checksumEvery: 30 };

export interface RollbackStats {
  rollbacks: number;
  maxDepth: number;
  totalDepth: number;
  /** Кадры, в которые игра ждала соперника (за окном отката). */
  stalls: number;
  /** Кадры, пропущенные для выравнивания часов. */
  waits: number;
  checksumsCompared: number;
  desyncs: number;
  /** Пинг туда-обратно, кадры (оценка по подтверждениям). */
  rttFrames: number;
}

const RING = 256;
const NONE = -1;

/** Кольцо значений по номеру кадра: хранит, какой кадр лежит в ячейке. */
class FrameRing<T> {
  private readonly values: (T | undefined)[] = new Array<T | undefined>(RING);
  private readonly frames = new Int32Array(RING).fill(NONE);

  set(frame: number, value: T): void {
    this.values[frame % RING] = value;
    this.frames[frame % RING] = frame;
  }

  get(frame: number): T | undefined {
    return frame >= 0 && this.frames[frame % RING] === frame ? this.values[frame % RING] : undefined;
  }
}

/**
 * Откат кадров по схеме GGPO для двоих. Свои нажатия — с задержкой inputDelay, нажатия соперника предсказываются
 * («держит то же»), а когда приходят настоящие и не совпали — мир откатывается к тому кадру и пересчитывается.
 * Каналу не нужен порядок и надёжность: в каждом пакете — все неподтверждённые нажатия.
 */
export class RollbackSession {
  state: MatchState;
  readonly stats: RollbackStats = {
    rollbacks: 0,
    maxDepth: 0,
    totalDepth: 0,
    stalls: 0,
    waits: 0,
    checksumsCompared: 0,
    desyncs: 0,
    rttFrames: 0,
  };
  private readonly local = new FrameRing<number>();
  private readonly remote = new FrameRing<number>();
  private readonly usedRemote = new FrameRing<number>();
  private readonly snapshots = new FrameRing<MatchState>();
  private readonly sentAt = new FrameRing<number>();
  private readonly myChecksums = new FrameRing<number>();
  private readonly theirChecksums = new FrameRing<number>();
  private readonly remoteSide: 0 | 1;
  /** Последний кадр, до которого нажатия соперника пришли без дыр. */
  private remoteConfirmed = NONE;
  /** До какого кадра соперник получил наши нажатия. */
  private remoteAck = NONE;
  private remoteFrame = 0;
  private remoteLead = 0;
  private rollbackFrom = Number.POSITIVE_INFINITY;
  private ticks = 0;
  private lastWait = 0;
  private readonly presented = new Map<string, number>();
  onDesync: ((frame: number) => void) | null = null;

  constructor(
    private readonly rules: MatchRules,
    seed: number,
    private readonly side: 0 | 1,
    private readonly transport: Transport,
    private readonly options: RollbackOptions = DEFAULT_ROLLBACK,
  ) {
    this.state = createMatch(rules, seed);
    this.remoteSide = side === 0 ? 1 : 0;
    for (let frame = 0; frame < options.inputDelay; frame++) this.local.set(frame, 0);
    transport.onMessage((bytes) => {
      this.receive(bytes);
    });
  }

  get frame(): number {
    return this.state.frame;
  }

  /** Сколько кадров мир ушёл вперёд от подтверждённых нажатий соперника. */
  get predictedFrames(): number {
    return this.state.frame - (this.remoteConfirmed + 1);
  }

  private receive(bytes: Uint8Array): void {
    const message = decode(bytes);
    if (message?.type !== "inputs") return;
    message.inputs.forEach((bits, index) => {
      const frame = message.start + index;
      if (this.remote.get(frame) !== undefined) return;
      this.remote.set(frame, bits);
      const used = this.usedRemote.get(frame);
      if (frame < this.state.frame && used !== undefined && used !== bits)
        this.rollbackFrom = Math.min(this.rollbackFrom, frame);
    });
    while (this.remote.get(this.remoteConfirmed + 1) !== undefined) this.remoteConfirmed++;
    if (message.ack > this.remoteAck) {
      const sent = this.sentAt.get(message.ack);
      if (sent !== undefined) this.stats.rttFrames = this.ticks - sent;
      this.remoteAck = message.ack;
    }
    this.remoteFrame = Math.max(this.remoteFrame, message.frame);
    this.remoteLead = message.lead;
    if (message.checksumFrame >= 0) {
      this.theirChecksums.set(message.checksumFrame, message.checksum);
      this.compareChecksum(message.checksumFrame);
    }
  }

  private readonly compared = new FrameRing<boolean>();

  private compareChecksum(frame: number): void {
    const mine = this.myChecksums.get(frame);
    const theirs = this.theirChecksums.get(frame);
    if (mine === undefined || theirs === undefined || this.compared.get(frame)) return;
    this.compared.set(frame, true);
    this.stats.checksumsCompared++;
    if (mine !== theirs) {
      this.stats.desyncs++;
      this.onDesync?.(frame);
    }
  }

  private inputsFor(frame: number): [InputBits, InputBits] {
    const mine = this.local.get(frame) ?? 0;
    const theirs = this.remote.get(frame) ?? this.remote.get(this.remoteConfirmed) ?? 0;
    this.usedRemote.set(frame, theirs);
    return this.side === 0 ? [mine, theirs] : [theirs, mine];
  }

  private present(events: readonly SimEvent[], out: SimEvent[]): void {
    for (const event of events) {
      const key = `${event.frame}|${event.kind}|${event.who}|${event.ability ?? ""}`;
      if (this.presented.has(key)) continue;
      this.presented.set(key, event.frame);
      out.push(event);
    }
  }

  private simulate(out: SimEvent[]): void {
    const frame = this.state.frame;
    this.snapshots.set(frame, cloneMatch(this.state));
    this.present(step(this.state, this.rules, this.inputsFor(frame)), out);
  }

  private rollback(out: SimEvent[]): void {
    if (!Number.isFinite(this.rollbackFrom)) return;
    const from = this.rollbackFrom;
    this.rollbackFrom = Number.POSITIVE_INFINITY;
    const snapshot = this.snapshots.get(from);
    if (!snapshot) throw new Error(`Нет снимка кадра ${from} для отката`);
    const target = this.state.frame;
    const depth = target - from;
    this.stats.rollbacks++;
    this.stats.totalDepth += depth;
    this.stats.maxDepth = Math.max(this.stats.maxDepth, depth);
    this.state = cloneMatch(snapshot);
    while (this.state.frame < target) this.simulate(out);
  }

  /** Подтверждённый кадр, чей снимок уже окончателен: все нажатия до него известны. */
  private finalChecksum(): void {
    const every = this.options.checksumEvery;
    const frame = Math.floor(Math.min(this.remoteConfirmed + 1, this.state.frame) / every) * every;
    if (frame <= 0 || this.myChecksums.get(frame) !== undefined) return;
    const snapshot = this.snapshots.get(frame);
    if (!snapshot) return;
    this.myChecksums.set(frame, hashMatch(snapshot));
    this.compareChecksum(frame);
  }

  private send(): void {
    const newest = this.state.frame + this.options.inputDelay - 1;
    const start = Math.max(this.remoteAck + 1, newest - MAX_INPUTS_PER_PACKET + 1, 0);
    const inputs: number[] = [];
    for (let frame = start; frame <= newest; frame++) inputs.push(this.local.get(frame) ?? 0);
    const every = this.options.checksumEvery;
    const checksumFrame = Math.floor(Math.min(this.remoteConfirmed + 1, this.state.frame) / every) * every;
    const checksum = this.myChecksums.get(checksumFrame);
    this.transport.send(
      encode({
        type: "inputs",
        start,
        inputs,
        ack: this.remoteConfirmed,
        frame: this.state.frame,
        lead: this.state.frame - this.remoteFrame,
        checksumFrame: checksum === undefined ? -1 : checksumFrame,
        checksum: checksum ?? 0,
      }),
    );
  }

  /**
   * Один кадр экрана: принять своё нажатие, откатить при расхождении, шагнуть вперёд, если можно.
   * Возвращает новые события для эффектов и звука и шагнул ли мир.
   */
  tick(localBits: InputBits): { events: SimEvent[]; advanced: boolean } {
    this.ticks++;
    const events: SimEvent[] = [];
    this.rollback(events);
    const scheduled = this.state.frame + this.options.inputDelay;
    if (this.local.get(scheduled) === undefined) {
      this.local.set(scheduled, localBits);
      this.sentAt.set(scheduled, this.ticks);
    }
    let advanced = false;
    const ahead = (this.state.frame - this.remoteFrame - this.remoteLead) / 2;
    if (this.predictedFrames >= this.options.maxRollback) {
      this.stats.stalls++;
    } else if (ahead >= 1.5 && this.ticks - this.lastWait > 20) {
      this.stats.waits++;
      this.lastWait = this.ticks;
    } else {
      this.simulate(events);
      advanced = true;
    }
    this.finalChecksum();
    this.send();
    for (const [key, frame] of this.presented) if (frame < this.state.frame - 120) this.presented.delete(key);
    return { events, advanced };
  }
}
